import { useDispatch } from "react-redux";
import { onChangeUnderwritingProvision, setPolicyInformationType } from "../../../../../features/ebam/plicy-data-page/policy-information/policy-information.slice";
import EditSaveCancelButtons from "../../../../ui/edit-save-cancel-buttons";
import Input from "../../../../ui/input";
import { customStyle, defaultStyle } from "../../../../../constant/custom-style";
import PolicyCKEditorTextModal from "../../modal/policy-ck-editor-text-modal";
import { usePolicyActions } from "../../../../../hooks/actions/use-policy-actions";
import LefSideLabel from "../../../../ui/left-side-label";
import PolicyContentHeaderAction from "./policy-content-header-action";
import { usePolicyDataState } from "../../../../../hooks/policy/use-policy-data-state";

interface Props {
    isShow: boolean;
}

export default function PolicyInformationUnderwritingProvisions({ isShow }: Props) {

    const dispatch = useDispatch()
    const {
        isPIUnderwritingProvisions,
        policyUnderwritingProvisions,
        isPIUnderwritingProvisionsNotes,
        policyUnderwritingProvisionsNotes
    } = usePolicyDataState()
    const { 
        handleEditPolicyInformation, 
        isLoadingUpdatePolicy, 
        handleCancelEditPolicyInformation
    } = usePolicyActions()
    
    if (!isShow) {
        return null;
    }

    return (
        <div className='rounded border border-gray-200'>
            <div className='bg-[#eef6fc] px-3 py-3 rounded border-b'>
                <div className="flex flex-row items-start justify-between gap-2 md:gap-4">
                    <PolicyContentHeaderAction
                        title='Underwriting Provisions'
                        subTitle='Provide the underwriting limits and age eligibility.'
                    />
                    <EditSaveCancelButtons
                        isEditing={!isPIUnderwritingProvisions}
                        onEdit={() => dispatch(setPolicyInformationType("underwritingProvisions"))}
                        onCancel={handleCancelEditPolicyInformation}
                        onSave={() => handleEditPolicyInformation('underwritingProvisions')}
                        isLoading={isLoadingUpdatePolicy}
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 gap-5">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px] border-collapse text-xs">
                        <thead>
                            <tr className="bg-[#eef6fc]">
                                <th className="w-[35%] border-b border-r border-[#c7e0f3] px-3 py-2 text-left font-semibold text-[#186cb2] sm:px-4 sm:py-2.5">
                                    Parameters
                                </th>
                                <th className="w-[35%] border-b border-r border-[#c7e0f3] px-3 py-2 text-center font-semibold text-[#186cb2] sm:px-4 sm:py-2.5">
                                    Maximum Amount of Insurance at
                                    entry
                                </th>
                                <th className="w-[30%] border-b border-[#c7e0f3] px-3 py-2 text-center font-semibold text-[#186cb2] sm:px-4 sm:py-2.5">
                                    Attained Age at entry
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {policyUnderwritingProvisions.map((data) => {
                                const isMedical = data.id === 3;
                                return (
                                    <tr key={data.id}>
                                        <td className="border-b border-r border-gray-200 px-3 py-2 text-gray-600 sm:px-4">
                                            {data.title}
                                        </td>
                                        {isMedical ? (
                                            <td colSpan={2} className="border-b border-r border-gray-200 p-2" >
                                                <Input
                                                    type="text"
                                                    name={`underwritingProvision-${data.id}`}
                                                    value={data.description ?? ""}
                                                    isCustomStyle={true}
                                                    customCss={`${defaultStyle} ${!data.description ? "" : customStyle}`}
                                                    onChange={(e) => {
                                                        dispatch(onChangeUnderwritingProvision({
                                                            id: data.id,
                                                            field: "description",
                                                            value: e.target.value,
                                                        }));
                                                    }}
                                                    required={true}
                                                    disabled={!isPIUnderwritingProvisions || isLoadingUpdatePolicy}
                                                />
                                                {/* <div className="">
                                                    <RichTextViewer value={data.description ?? ''} />
                                                </div> */}
                                            </td>
                                        ) : (
                                            <>
                                                {/* Amount */}
                                                <td className="border-b border-r border-gray-200 p-2">
                                                    <div className={`flex overflow-hidden rounded-md border focus-within:border-[#186cb2] border-gray-300`} >
                                                        {/* Peso */}
                                                        <LefSideLabel label="₱"/>
                                                        {/* Amount Input */}
                                                        <Input
                                                            type="number"
                                                            name={`underwritingProvision-amount-${data.id}`}
                                                            value={data.amount ?? ""}
                                                            // value={formatNumberWithComma(data.amount ?? "")}
                                                            isCustomStyle={true}
                                                            customCss={`w-full rounded-tr rounded-br px-3 py-2 text-xs outline-none ${!data.amount ? "" : "bg-[#186cb2]/10" }`}
                                                            onChange={(e) => {
                                                                dispatch(onChangeUnderwritingProvision({
                                                                    id: data.id,
                                                                    field: "amount",
                                                                    value: e.target.value,
                                                                }));
                                                            }}
                                                            required={true}
                                                            disabled={!isPIUnderwritingProvisions || isLoadingUpdatePolicy}
                                                        />
                                                    </div>
                                                </td>
                                                {/* Age Range */}
                                                <td className="border-b border-gray-200 p-2">
                                                    <Input
                                                        type="text"
                                                        name={`underwritingProvision-age-${data.id}`}
                                                        value={data.ageRange ?? ""}
                                                        isCustomStyle={true}
                                                        customCss={`${defaultStyle} ${!data.ageRange ? "" : customStyle}`}
                                                        onChange={(e) => {
                                                            dispatch(onChangeUnderwritingProvision({
                                                                id: data.id,
                                                                field: "ageRange",
                                                                value: e.target.value,
                                                            }));
                                                        }}
                                                        required={true}
                                                        disabled={!isPIUnderwritingProvisions || isLoadingUpdatePolicy}
                                                    />
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <PolicyCKEditorTextModal
                title='Underwriting Provisions Notes'
                description='Add notes or additional remarks if necessary.'
                isEditing={isPIUnderwritingProvisionsNotes}
                onEdit={() => dispatch(setPolicyInformationType("underwritingProvisionsNotes"))}
                onCancel={() => dispatch(setPolicyInformationType(null))}
                onSave={() => dispatch(setPolicyInformationType(null))}
                value={policyUnderwritingProvisionsNotes}
                parentStyle="overflow-hidden"
            />
        </div>
    )
}
